import { z } from 'zod';
import { ModelId } from './enums';

export const TurnAccepted = z.object({
  turnId: z.uuid(),
  messageId: z.uuid(),
  model: ModelId,
  charged: z.number().int().min(0),
  balance: z.number().int(),
});
export const Delta = z.object({ text: z.string() });
export const MessageDone = z.object({
  content: z.object({ text: z.string() }),
  usage: z.object({ inputTokens: z.number().int(), outputTokens: z.number().int() }),
  balance: z.number().int(),
});
export const SseError = z.object({
  code: z.enum(['LLM_FAILED', 'LLM_TIMEOUT', 'CONTENT_FILTERED', 'INTERNAL']),
  message: z.string(),
  refunded: z.number().int().optional(),
  balance: z.number().int().optional(),
  retryable: z.boolean(),
});
export const Done = z.object({});

export const SseEventSchemas = {
  'turn.accepted': TurnAccepted,
  delta: Delta,
  'message.done': MessageDone,
  error: SseError,
  done: Done,
} as const;

export type SseEvent =
  | { event: 'turn.accepted'; data: z.infer<typeof TurnAccepted> }
  | { event: 'delta'; data: z.infer<typeof Delta> }
  | { event: 'message.done'; data: z.infer<typeof MessageDone> }
  | { event: 'error'; data: z.infer<typeof SseError> }
  | { event: 'done'; data: z.infer<typeof Done> };

type SseEventName = keyof typeof SseEventSchemas;
const isKnownEvent = (name: string): name is SseEventName => Object.hasOwn(SseEventSchemas, name);

/**
 * `text/event-stream` 바디를 SseEvent 로 바꾼다.
 * - 청크 경계·멀티바이트 안전, 멀티라인 data 는 `\n` 으로 잇는다.
 * - `: ping` 코멘트, `id`·`retry` 필드, 알 수 없는 event 는 건너뛴다.
 * - JSON·zod 검증 실패는 그대로 throw 한다 (error 이벤트로 바꾸지 않는다).
 */
export async function* parseSseStream(stream: ReadableStream<Uint8Array>): AsyncIterable<SseEvent> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let eventName = '';
  let dataLines: string[] = [];

  const dispatch = (): SseEvent | undefined => {
    const name = eventName;
    const lines = dataLines;
    eventName = '';
    dataLines = [];
    if (lines.length === 0 || !isKnownEvent(name)) return undefined;
    const data: unknown = JSON.parse(lines.join('\n'));
    return { event: name, data: SseEventSchemas[name].parse(data) } as SseEvent;
  };

  const handleLine = (raw: string): SseEvent | undefined => {
    const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw;
    if (line === '') return dispatch();
    if (line.startsWith(':')) return undefined;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') eventName = value;
    else if (field === 'data') dataLines.push(value);
    return undefined;
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf('\n')) !== -1) {
        const ev = handleLine(buffer.slice(0, nl));
        buffer = buffer.slice(nl + 1);
        if (ev) yield ev;
      }
    }
    buffer += decoder.decode();
    if (buffer.length > 0) {
      const ev = handleLine(buffer);
      if (ev) yield ev;
    }
    const last = dispatch();
    if (last) yield last;
  } finally {
    reader.releaseLock();
  }
}
